// ==========================================================================
// Mixpanel Service - Thin wrapper for analytics
// No-ops gracefully when token is not configured
// ==========================================================================

import mixpanel from 'mixpanel-browser';
import type { User } from '@/types/user';
import { config } from '@/lib/config';

let initialized = false;

/**
 * Initialize Mixpanel with EU endpoint. No-op if no token configured.
 */
export function initMixpanel(): void {
  const token = config.mixpanelToken;
  if (!token) {
    if (config.debug) console.log('[Mixpanel] No token configured, analytics disabled');
    return;
  }

  // Route Mixpanel traffic through Yaya Engine's `/api/mixpanel-proxy/...`
  // endpoint so ad blockers and tracker-protection extensions don't intercept
  // it. We also override the SDK's default `/track/` and `/engage/` route
  // names because some blockers (Opera GX, uBlock) pattern-match on those
  // strings in any URL — even first-party ones. The BE maps these short
  // codes back to the real Mixpanel endpoints.
  // Locally we still hit Mixpanel directly because the BE proxy may not be
  // reachable from `vite dev` with a different API URL.
  const apiHost = config.isLocal
    ? 'https://api-eu.mixpanel.com'
    : `${config.apiUrl}/mixpanel-proxy`;

  mixpanel.init(token, {
    api_host: apiHost,
    api_routes: config.isLocal
      ? undefined
      : { track: 't1/', engage: 't2/', groups: 't3/', record: 't4/' },
    persistence: 'localStorage',
    track_pageview: false,
    debug: config.debug,
  });

  initialized = true;
  if (config.debug) console.log('[Mixpanel] Initialized');
}

/**
 * Identify the user immediately after auth (OTP / magic link verify) using
 * just the id from the response. This binds the Mixpanel distinct_id to the
 * user before they hit any tracked actions in onboarding, so events show
 * the user id rather than `$device:...` in Live View.
 *
 * The full identifyUser (with people profile + super properties) is called
 * later when the user object is fully fetched — Mixpanel's identify is
 * idempotent so both calls are safe.
 */
export function identifyById(userId: number | string): void {
  if (!initialized) return;
  mixpanel.identify(String(userId));
}

/**
 * Identify the current user and set people profile + super properties.
 */
export function identifyUser(user: User): void {
  if (!initialized) return;

  mixpanel.identify(String(user.id));

  mixpanel.people.set({
    $name: user.name,
    $phone: user.phone_number,
    FSP: user.fsp.name,
    'FSP Slug': user.fsp.slug,
    Locale: user.locale,
    Onboarded: user.onboarded,
    Verified: user.verified,
    'Alert Subscription Status': user.alert_subscription_status,
    'Has Location': !!user.location,
    Municipality: user.location?.municipality || null,
    Platform: 'pwa',
  });

  mixpanel.register({
    fsp_name: user.fsp.name,
    fsp_slug: user.fsp.slug,
    locale: user.locale,
    alert_subscription_status: user.alert_subscription_status,
    onboarded: user.onboarded,
    verified: user.verified,
    platform: 'pwa',
    channel: 'pwa',
  });
}

/**
 * Clear identity on logout.
 */
export function resetMixpanel(): void {
  if (!initialized) return;
  mixpanel.reset();
}

/**
 * Track an event. No-op if not initialized.
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  mixpanel.track(event, properties);
}
